import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  SeedResponse,
  CreateSeedRequest,
  UpdateSeedRequest,
  FilterState,
  VoiceSeedConfig,
} from '../types';
import {
  listSeeds,
  getSeed,
  createSeed,
  updateSeed,
  deleteSeed,
  fetchAllSeeds,
  listTags,
} from '../api';
import { useScope } from '../scopeContext';
import ProjectPicker from './ProjectPicker';
import ProjectSelect from './ProjectSelect';
import TagFilterBar from './TagFilterBar';
import VoiceConfigForm from './VoiceConfigForm';

function seedToFormData(seed: SeedResponse, nameOverride?: string): SeedFormData {
  return {
    name: nameOverride ?? seed.name,
    scenario_id: seed.scenario_id,
    agent_id: seed.agent_id,
    initial_prompt: seed.initial_prompt || '',
    max_turns: seed.max_turns,
    llm_persona: seed.llm_persona || '',
    session_data: seed.session_data ? JSON.stringify(seed.session_data, null, 2) : '{}',
    agents_overrides: seed.agents_overrides ? JSON.stringify(seed.agents_overrides, null, 2) : '{}',
    memory_service_info:
      seed.memory_service_info && Object.keys(seed.memory_service_info).length > 0
        ? JSON.stringify(seed.memory_service_info, null, 2)
        : '',
    language: seed.language || '',
    dynamic_language: seed.dynamic_language ?? false,
    timezone: seed.timezone || '',
    stop_phrase_regexp: seed.stop_phrase_regexp || '',
    stop_tool_names: seed.stop_tool_names?.join(', ') ?? '',
    tags_csv: seed.tags?.join(', ') ?? '',
    config: seed.config?.type === 'voice' ? seed.config : null,
    project_id: seed.project_id,
  };
}

function isRecord(x: unknown): x is Record<string, unknown> {
  return x !== null && typeof x === 'object' && !Array.isArray(x);
}

interface SeedFormData {
  name: string;
  scenario_id: string;
  agent_id: string;
  initial_prompt: string;
  max_turns: number;
  llm_persona: string;
  session_data: string;
  agents_overrides: string;
  memory_service_info: string;
  language: string;
  dynamic_language: boolean;
  timezone: string;
  stop_phrase_regexp: string;
  stop_tool_names: string;
  tags_csv: string;
  config: VoiceSeedConfig | null;
  project_id: string | null;
}

const emptySeedForm: SeedFormData = {
  name: '',
  scenario_id: '',
  agent_id: '',
  initial_prompt: '',
  max_turns: 10,
  llm_persona: '',
  session_data: '{}',
  agents_overrides: '{}',
  memory_service_info: '',
  language: '',
  dynamic_language: false,
  timezone: '',
  stop_phrase_regexp: '',
  stop_tool_names: '',
  tags_csv: '',
  config: null,
  project_id: null,
};

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};

const TIMEZONE_OPTIONS: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf(key: string): string[] }).supportedValuesOf('timeZone');
  } catch {
    return [];
  }
})();

function seedToExportRecord(seed: SeedResponse): CreateSeedRequest {
  return {
    name: seed.name,
    scenario_id: seed.scenario_id,
    agent_id: seed.agent_id,
    initial_prompt: seed.initial_prompt ?? undefined,
    max_turns: seed.max_turns,
    llm_persona: seed.llm_persona ?? undefined,
    session_data: seed.session_data ?? undefined,
    agents_overrides: seed.agents_overrides ?? undefined,
    language: seed.language?.trim() || undefined,
    dynamic_language: seed.dynamic_language ?? false,
    memory_service_info:
      seed.memory_service_info && Object.keys(seed.memory_service_info).length > 0
        ? seed.memory_service_info
        : undefined,
    timezone: seed.timezone?.trim() || undefined,
    stop_phrase_regexp: seed.stop_phrase_regexp ?? undefined,
    stop_tool_names:
      seed.stop_tool_names && seed.stop_tool_names.length > 0
        ? seed.stop_tool_names
        : undefined,
    tags: seed.tags && seed.tags.length > 0 ? seed.tags : undefined,
  };
}

function parseSeedImportItem(item: unknown): CreateSeedRequest | null {
  if (!isRecord(item)) return null;
  const name = item.name;
  const scenario_id = item.scenario_id;
  const agent_id = item.agent_id;
  if (typeof name !== 'string' || !name.trim()) return null;
  if (typeof scenario_id !== 'string' || !scenario_id.trim()) return null;
  if (typeof agent_id !== 'string' || !agent_id.trim()) return null;

  let max_turns = 10;
  if (item.max_turns !== undefined) {
    if (typeof item.max_turns !== 'number' || !Number.isInteger(item.max_turns)) return null;
    max_turns = item.max_turns;
  }

  let session_data: Record<string, unknown> | null | undefined;
  if (item.session_data !== undefined && item.session_data !== null) {
    if (!isRecord(item.session_data)) return null;
    session_data = item.session_data;
  } else if (item.session_data === null) {
    session_data = null;
  }

  let agents_overrides: Record<string, unknown> | null | undefined;
  if (item.agents_overrides !== undefined && item.agents_overrides !== null) {
    if (!isRecord(item.agents_overrides)) return null;
    agents_overrides = item.agents_overrides;
  } else if (item.agents_overrides === null) {
    agents_overrides = null;
  }

  let memory_service_info: Record<string, Record<string, string>> | undefined;
  if (item.memory_service_info !== undefined && item.memory_service_info !== null) {
    if (!isRecord(item.memory_service_info)) return null;
    const mem: Record<string, Record<string, string>> = {};
    for (const [k, v] of Object.entries(item.memory_service_info)) {
      if (!isRecord(v)) return null;
      const inner: Record<string, string> = {};
      for (const [ik, iv] of Object.entries(v)) {
        if (typeof iv !== 'string') return null;
        inner[ik] = iv;
      }
      mem[k] = inner;
    }
    memory_service_info = mem;
  }

  if (
    item.initial_prompt !== undefined &&
    item.initial_prompt !== null &&
    typeof item.initial_prompt !== 'string'
  ) {
    return null;
  }
  const initial_prompt =
    item.initial_prompt === undefined || item.initial_prompt === null
      ? undefined
      : item.initial_prompt;

  if (
    item.llm_persona !== undefined &&
    item.llm_persona !== null &&
    typeof item.llm_persona !== 'string'
  ) {
    return null;
  }
  const llm_persona =
    item.llm_persona === undefined || item.llm_persona === null
      ? undefined
      : item.llm_persona;

  if (
    item.language !== undefined &&
    item.language !== null &&
    typeof item.language !== 'string'
  ) {
    return null;
  }
  const language =
    item.language === undefined || item.language === null ? undefined : item.language;

  if (
    item.timezone !== undefined &&
    item.timezone !== null &&
    typeof item.timezone !== 'string'
  ) {
    return null;
  }
  const timezone =
    item.timezone === undefined || item.timezone === null ? undefined : item.timezone;

  const dynamic_language =
    typeof item.dynamic_language === 'boolean' ? item.dynamic_language : false;

  if (
    item.stop_phrase_regexp !== undefined &&
    item.stop_phrase_regexp !== null &&
    typeof item.stop_phrase_regexp !== 'string'
  ) {
    return null;
  }
  const stop_phrase_regexp =
    item.stop_phrase_regexp === undefined || item.stop_phrase_regexp === null
      ? undefined
      : item.stop_phrase_regexp;

  let stop_tool_names: string[] | undefined;
  if (item.stop_tool_names !== undefined && item.stop_tool_names !== null) {
    if (!Array.isArray(item.stop_tool_names)) return null;
    for (const entry of item.stop_tool_names) {
      if (typeof entry !== 'string') return null;
    }
    stop_tool_names = (item.stop_tool_names as string[]).filter(Boolean);
    if (stop_tool_names.length === 0) stop_tool_names = undefined;
  }

  return {
    name: name.trim(),
    scenario_id: scenario_id.trim(),
    agent_id: agent_id.trim(),
    initial_prompt,
    max_turns,
    llm_persona,
    session_data,
    agents_overrides,
    language: language?.trim() || undefined,
    dynamic_language,
    memory_service_info,
    timezone: timezone?.trim() || undefined,
    stop_phrase_regexp,
    stop_tool_names,
  };
}

const SeedManager: React.FC = () => {
  const PAGE_SIZE = 100;
  const SCROLL_BOTTOM_THRESHOLD = 300;
  const intl = useIntl();
  const navigate = useNavigate();
  const { seedId } = useParams<{ seedId?: string }>();
  const [seeds, setSeeds] = useState<SeedResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [editingSeed, setEditingSeed] = useState<SeedResponse | null>(null);
  const [formData, setFormData] = useState<SeedFormData>(emptySeedForm);
  const [importMessage, setImportMessage] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<FilterState>({
    text_search: '',
    include_tags: [],
    exclude_tags: [],
    untagged: 'all',
  });
  const [tags, setTags] = useState<string[]>([]);
  const { scope, refreshProjects } = useScope();
  const fetchSeeds = useCallback(
    async (reset: boolean) => {
      if (reset) {
        setLoading(true);
        setError('');
      } else {
        setLoadingMore(true);
      }

      try {
        const offset = reset ? 0 : seeds.length;
        const response = await listSeeds(PAGE_SIZE, offset, filter, scope);
        setTotal(response.total);
        setHasMore(offset + response.seeds.length < response.total);
        setSeeds((prev) => (reset ? response.seeds : [...prev, ...response.seeds]));
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to fetch seeds';
        setError(message);
      } finally {
        if (reset) {
          setLoading(false);
        } else {
          setLoadingMore(false);
        }
      }
    },
    [filter, scope, seeds.length]
  );

  const openEditForm = useCallback((seed: SeedResponse) => {
    setEditingSeed(seed);
    setFormData(seedToFormData(seed));
    setShowForm(true);
  }, []);

  useEffect(() => {
    void fetchSeeds(true);
  }, [fetchSeeds]);
  useEffect(() => {
    const onScroll = () => {
      if (loading || loadingMore || !hasMore || showForm) {
        return;
      }
      const scrolledToBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - SCROLL_BOTTOM_THRESHOLD;
      if (scrolledToBottom) {
        void fetchSeeds(false);
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [fetchSeeds, hasMore, loading, loadingMore, showForm]);
  useEffect(() => {
    void listTags(scope).then((res) => setTags(res.tags)).catch(() => setTags([]));
  }, [scope]);

  useEffect(() => {
    if (!seedId) return;
    if (editingSeed?.seed_id === seedId) return;
    const cached = seeds.find((s) => s.seed_id === seedId);
    if (cached) {
      openEditForm(cached);
      return;
    }
    let cancelled = false;
    void getSeed(seedId)
      .then((seed) => {
        if (!cancelled) openEditForm(seed);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load seed';
        setError(message);
      });
    return () => {
      cancelled = true;
    };
  }, [seedId, seeds, editingSeed, openEditForm]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : name === 'max_turns'
            ? parseInt(value, 10) || 0
            : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let sessionData: Record<string, unknown> | null = null;
      if (formData.session_data.trim()) {
        try {
          sessionData = JSON.parse(formData.session_data);
        } catch {
          setError('Invalid JSON in session_data');
          setLoading(false);
          return;
        }
      }

      let agentsOverrides: Record<string, unknown> | null = null;
      if (formData.agents_overrides.trim()) {
        try {
          agentsOverrides = JSON.parse(formData.agents_overrides);
        } catch {
          setError('Invalid JSON in agents_overrides');
          setLoading(false);
          return;
        }
      }

      let memoryServiceInfo: Record<string, Record<string, string>> | null = null;
      if (formData.memory_service_info.trim()) {
        try {
          memoryServiceInfo = JSON.parse(formData.memory_service_info);
        } catch {
          setError('Invalid JSON in memory_service_info');
          setLoading(false);
          return;
        }
      }

      const stopPhraseRegexp = formData.stop_phrase_regexp.trim() || null;

      const stopToolNames = formData.stop_tool_names
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const stopToolNamesValue = stopToolNames.length > 0 ? stopToolNames : null;

      if (editingSeed) {
        const updateData: UpdateSeedRequest = {
          name: formData.name || null,
          scenario_id: formData.scenario_id || null,
          agent_id: formData.agent_id || null,
          initial_prompt: formData.initial_prompt || null,
          max_turns: formData.max_turns || null,
          llm_persona: formData.llm_persona || null,
          config: formData.config ?? { type: 'text' },
          session_data: sessionData,
          agents_overrides: agentsOverrides,
          memory_service_info: memoryServiceInfo,
          language: formData.language.trim() || null,
          dynamic_language: formData.dynamic_language,
          timezone: formData.timezone.trim() || null,
          stop_phrase_regexp: stopPhraseRegexp,
          stop_tool_names: stopToolNamesValue,
          tags: formData.tags_csv.split(',').map((item) => item.trim()).filter(Boolean),
          project_id: formData.project_id,
        };
        await updateSeed(editingSeed.seed_id, updateData);
      } else {
        const createData: CreateSeedRequest = {
          name: formData.name,
          scenario_id: formData.scenario_id,
          agent_id: formData.agent_id,
          initial_prompt: formData.initial_prompt || undefined,
          max_turns: formData.max_turns,
          llm_persona: formData.llm_persona || undefined,
          config: formData.config ?? undefined,
          session_data: sessionData,
          agents_overrides: agentsOverrides,
          memory_service_info: memoryServiceInfo ?? undefined,
          language: formData.language.trim() || undefined,
          dynamic_language: formData.dynamic_language,
          timezone: formData.timezone.trim() || undefined,
          stop_phrase_regexp: stopPhraseRegexp ?? undefined,
          stop_tool_names: stopToolNamesValue ?? undefined,
          tags: formData.tags_csv.split(',').map((item) => item.trim()).filter(Boolean),
          project_id: formData.project_id ?? undefined,
        };
        await createSeed(createData);
      }

      setShowForm(false);
      setEditingSeed(null);
      setFormData(emptySeedForm);
      if (seedId) navigate('/seeds', { replace: true });
      refreshProjects();
      await fetchSeeds(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save seed';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (seed: SeedResponse) => {
    openEditForm(seed);
    navigate(`/seeds/${seed.seed_id}`, { replace: true });
  };

  const handleDelete = async (seedId: string) => {
    if (!confirm('Delete this seed?')) return;
    setLoading(true);
    setError('');
    try {
      await deleteSeed(seedId);
      await fetchSeeds(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete seed';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (seed: SeedResponse) => {
    setEditingSeed(null);
    setFormData(seedToFormData(seed, `${seed.name} (copy)`));
    setShowForm(true);
    if (seedId) navigate('/seeds', { replace: true });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingSeed(null);
    setFormData(emptySeedForm);
    if (seedId) navigate('/seeds', { replace: true });
  };

  const handleDownloadSeeds = async () => {
    setLoading(true);
    setError('');
    setImportMessage('');
    try {
      const all = await fetchAllSeeds(filter, scope);
      const payload = all.map(seedToExportRecord);
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'self-play-seeds.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : intl.formatMessage({
              id: 'errors.failedToExportSeeds',
              defaultMessage: 'Failed to export seeds',
            });
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedsFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setLoading(true);
    setError('');
    setImportMessage('');

    try {
      let data: unknown;
      try {
        const text = await file.text();
        data = JSON.parse(text) as unknown;
      } catch {
        setError(
          intl.formatMessage({
            id: 'errors.couldNotParseJsonFile',
            defaultMessage: 'Could not parse JSON file.',
          })
        );
        return;
      }

      if (!Array.isArray(data)) {
        setError(
          intl.formatMessage({
            id: 'errors.seedsJsonMustBeArray',
            defaultMessage: 'JSON root must be an array of seed objects.',
          })
        );
        return;
      }

      let created = 0;
      let failed = 0;
      const failures: string[] = [];

      for (let i = 0; i < data.length; i += 1) {
        const parsed = parseSeedImportItem(data[i]);
        if (!parsed) {
          failed += 1;
          failures.push(
            intl.formatMessage(
              {
                id: 'errors.seedImportItemInvalid',
                defaultMessage:
                  'Item {index}: invalid or missing name, scenario_id, or agent_id.',
              },
              { index: i }
            )
          );
          continue;
        }
        try {
          await createSeed({ ...parsed, project_id: scope.project_id });
          created += 1;
        } catch (err: unknown) {
          failed += 1;
          const msg =
            err instanceof Error
              ? err.message
              : intl.formatMessage({
                  id: 'errors.requestFailed',
                  defaultMessage: 'Request failed',
                });
          failures.push(`"${parsed.name}": ${msg}`);
        }
      }

      refreshProjects();
      try {
        await fetchSeeds(true);
      } catch (err: unknown) {
        setError(
          err instanceof Error
            ? err.message
            : intl.formatMessage({
                id: 'errors.failedToRefreshSeedsAfterImport',
                defaultMessage:
                  'Import completed but the seed list could not be refreshed.',
              })
        );
      }

      const truncated = failures.slice(0, 5).join(' ');
      const ellipsis = failures.length > 5 ? ' …' : '';
      const detailSuffix = failures.length ? ` ${truncated}${ellipsis}` : '';
      setImportMessage(
        intl.formatMessage(
          {
            id: 'seedManager.importSummary',
            defaultMessage: 'Import finished: {created} created, {failed} failed.',
          },
          { created, failed }
        ) + detailSuffix
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <ProjectPicker />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18 }}>Seeds ({total})</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            style={{ display: 'none' }}
            onChange={(ev) => void handleSeedsFileSelected(ev)}
          />
          <button
            type="button"
            className="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading || !scope.project_id}
            style={{ background: '#e8f5e9' }}
          >
            <FormattedMessage
              id="evaluationManager.uploadJson"
              defaultMessage="Upload JSON"
            />
          </button>
          <button
            type="button"
            className="button"
            onClick={() => void handleDownloadSeeds()}
            disabled={loading}
            style={{ background: '#e3f2fd' }}
          >
            <FormattedMessage
              id="evaluationManager.downloadJson"
              defaultMessage="Download JSON"
            />
          </button>
          <button
            className="button button-primary"
            onClick={() => {
              setEditingSeed(null);
              setFormData({ ...emptySeedForm, project_id: scope.project_id });
              setShowForm(true);
            }}
            disabled={loading || !scope.project_id}
          >
            + New Seed
          </button>
        </div>
      </div>

      <TagFilterBar tags={tags} value={filter} onChange={setFilter} />

      {importMessage && (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            background: '#f0f7ff',
            color: '#1565c0',
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          {importMessage}
        </div>
      )}

      {error && (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            background: '#fff0f0',
            color: '#8b0000',
            borderRadius: 6,
          }}
        >
          {error}
        </div>
      )}

      {showForm && (
        <div
          style={{
            background: '#f8f9fa',
            padding: 16,
            borderRadius: 8,
            marginBottom: 16,
            border: '1px solid #e0e0e0',
          }}
        >
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>
            {editingSeed ? 'Edit Seed' : 'Create Seed'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Project
                </label>
                <ProjectSelect
                  value={formData.project_id}
                  onChange={(id) => setFormData((prev) => ({ ...prev, project_id: id }))}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Tags (comma-separated)
                </label>
                <input
                  type="text"
                  name="tags_csv"
                  value={formData.tags_csv}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                    Scenario ID *
                  </label>
                  <input
                    type="text"
                    name="scenario_id"
                    value={formData.scenario_id}
                    onChange={handleInputChange}
                    required
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                    Agent ID *
                  </label>
                  <input
                    type="text"
                    name="agent_id"
                    value={formData.agent_id}
                    onChange={handleInputChange}
                    required
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                  />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Initial Prompt
                </label>
                <textarea
                  name="initial_prompt"
                  value={formData.initial_prompt}
                  onChange={handleInputChange}
                  rows={3}
                  style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Max Turns
                </label>
                <input
                  type="number"
                  name="max_turns"
                  value={formData.max_turns}
                  onChange={handleInputChange}
                  min={1}
                  max={100}
                  style={{ width: 100, padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                    Language (BCP-47, e.g. en, ru, ky)
                  </label>
                  <input
                    type="text"
                    name="language"
                    value={formData.language}
                    onChange={handleInputChange}
                    placeholder="Optional default / fallback"
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                    Timezone
                  </label>
                  <select
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleInputChange}
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                  >
                    <option value="">— Optional, defaults to UTC —</option>
                    {TIMEZONE_OPTIONS.map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="dynamic_language"
                    checked={formData.dynamic_language}
                    onChange={handleInputChange}
                  />
                  Dynamic language (LLM returns message + language per turn)
                </label>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  LLM Persona
                </label>
                <textarea
                  name="llm_persona"
                  value={formData.llm_persona}
                  onChange={handleInputChange}
                  rows={3}
                  style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                />
              </div>
              <VoiceConfigForm
                value={formData.config}
                onChange={(next) => setFormData((prev) => ({ ...prev, config: next }))}
              />
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Session Data (JSON)
                </label>
                <textarea
                  name="session_data"
                  value={formData.session_data}
                  onChange={handleInputChange}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 4,
                    border: '1px solid #ddd',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: 12,
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Agents Overrides (JSON)
                </label>
                <textarea
                  name="agents_overrides"
                  value={formData.agents_overrides}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder='{"agent_name": {"key": "value"}}'
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 4,
                    border: '1px solid #ddd',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: 12,
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Memory Service Info (JSON)
                </label>
                <textarea
                  name="memory_service_info"
                  value={formData.memory_service_info}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder='{"public": {"key": "value"}, "private": {"key": "value"}}'
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 4,
                    border: '1px solid #ddd',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: 12,
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Stop Phrase Regexp
                </label>
                <input
                  type="text"
                  name="stop_phrase_regexp"
                  value={formData.stop_phrase_regexp}
                  onChange={handleInputChange}
                  placeholder="Sorry I can\.t hear you"
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 4,
                    border: '1px solid #ddd',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: 12,
                  }}
                />
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  Python-compatible regexp. When a bot message matches, the dialog ends immediately. Leave empty to disable.
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Stop Tool Names
                </label>
                <input
                  type="text"
                  name="stop_tool_names"
                  value={formData.stop_tool_names}
                  onChange={handleInputChange}
                  placeholder="transfer_to_call_human_agent_tool, end_conversation_tool"
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 4,
                    border: '1px solid #ddd',
                    fontFamily: 'Monaco, Consolas, monospace',
                    fontSize: 12,
                  }}
                />
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  Comma-separated tool names (supports tool_call and agent_switch / transfer_to_*_tool). When called, the tool call and accompanying bot message are saved, then the dialog ends. Leave empty to disable.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="submit"
                  className="button button-primary"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : editingSeed ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  className="button"
                  onClick={handleCancel}
                  disabled={loading}
                  style={{ background: '#e0e0e0' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {loading && !showForm && <div style={{ padding: 20, textAlign: 'center' }}>Loading...</div>}

      {!loading && seeds.length === 0 && (
        <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
          No seeds found. Create one to get started.
        </div>
      )}

      <div style={{ display: 'grid', gap: 8 }}>
        {seeds.map((seed) => (
          <div
            key={seed.seed_id}
            style={{
              background: '#fff',
              padding: 12,
              borderRadius: 8,
              border: '1px solid #e0e0e0',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{seed.name}</div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  Scenario: {seed.scenario_id} | Seed: {seed.seed_id} | Agent: {seed.agent_id} | Max Turns:{' '}
                  {seed.max_turns}
                  {seed.language && ` | Lang: ${seed.language}`}
                  {seed.dynamic_language && ' | Dynamic lang'}
                  {seed.timezone && ` | TZ: ${seed.timezone}`}
                  {seed.stop_phrase_regexp && ` | Stop: /${seed.stop_phrase_regexp}/`}
                  {seed.stop_tool_names && seed.stop_tool_names.length > 0 && ` | StopTools: ${seed.stop_tool_names.join(', ')}`}
                  {seed.tags && seed.tags.length > 0 && ` | Tags: ${seed.tags.join(', ')}`}
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  Created: {formatDate(seed.created_at)}
                  {seed.updated_at && ` | Updated: ${formatDate(seed.updated_at)}`}
                </div>
                {seed.initial_prompt && (
                  <div
                    style={{
                      fontSize: 12,
                      color: '#888',
                      marginTop: 4,
                      fontStyle: 'italic',
                    }}
                  >
                    "{seed.initial_prompt.substring(0, 80)}
                    {seed.initial_prompt.length > 80 ? '...' : ''}"
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  onClick={() => handleCopy(seed)}
                  style={{
                    padding: '4px 8px',
                    fontSize: 12,
                    background: '#e0e0e0',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                  title="Copy seed"
                >
                  Copy
                </button>
                <button
                  onClick={() => handleEdit(seed)}
                  style={{
                    padding: '4px 8px',
                    fontSize: 12,
                    background: '#2196F3',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(seed.seed_id)}
                  style={{
                    padding: '4px 8px',
                    fontSize: 12,
                    background: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {loadingMore && <div style={{ padding: 12, textAlign: 'center', color: '#666' }}>Loading more...</div>}
    </div>
  );
};

export default SeedManager;
